import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiUsers, FiX, FiFilter, FiArrowLeft, FiSearch, FiEye } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { agentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AgentProfileDetail from '../../components/agents/AgentProfileDetail';
import DataTable from '../../components/common/DataTable';
import { AGENT_STATUS_OPTIONS, getAgentStatusMeta } from '../../utils/agentStatus';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const AdminClients = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null); // For Modal Detail
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [filterManager, setFilterManager] = useState(null); // Manager object to filter by
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAPI.getAgents();
      // Filter ONLY agents that have NO user account
      const filteredAgents = data.filter(agent => !agent.user);
      setAgents(filteredAgents);
    } catch (error) {
      toast.error('ไม่สามารถดึงข้อมูลได้');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleViewDetail = async (agent) => {
    // Fetch full details before showing modal
    try {
        const { data } = await agentAPI.getAgent(agent.id);
        setSelectedAgent(data);
        setIsModalOpen(true);
    } catch (error) {
        console.error("Failed to fetch agent details", error);
        toast.error("ไม่สามารถดึงรายละเอียดได้");
        setSelectedAgent(agent); // Fallback
        setIsModalOpen(true);
    }
  };

  const handleManagerClick = (manager) => {
    if (manager) {
        setFilterManager(manager);
        toast.success(`แสดงลูกทีมของ ${manager.firstName} ${manager.lastName}`);
    }
  };

  const clearFilter = () => {
    setFilterManager(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const handleUpdateAgent = async (updatedData) => {
       try {
          const dataToSave = {
            ...updatedData,
            monthlySales: updatedData.monthlySales ? parseFloat(updatedData.monthlySales) : 0,
            yearlySales: updatedData.yearlySales ? parseFloat(updatedData.yearlySales) : 0,
          };
          
          await agentAPI.updateAgent(selectedAgent.id, dataToSave);
          
          // Update local list
          setAgents(prev => prev.map(a => a.id === selectedAgent.id ? {...a, ...dataToSave} : a));
          setSelectedAgent(dataToSave); // Update modal view
          toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
          return true;
        } catch (error) {
          console.error('Error updating agent:', error);
          toast.error('ไม่สามารถบันทึกข้อมูลได้');
          return false;
        }
  };

  // Filter Logic
  const filteredAgents = agents.filter((agent) =>
    (!filterManager || agent.manager?.id === filterManager.id) &&
    (filterStatus === 'all' || getAgentStatusMeta(agent.agentStatus).status === filterStatus)
  );

  const columns = useMemo(() => [
    {
      header: 'ชื่อ - นามสกุล',
      accessor: 'fullName',
      render: (_, row) => (
        <div className="font-medium text-gray-900">{row.firstName} {row.lastName}</div>
      )
    },
    {
      header: 'ชื่อเล่น',
      accessor: 'nickname',
      render: (val) => <span className="text-gray-500">{val || '-'}</span>
    },
    {
      header: 'รหัสตัวแทน',
      accessor: 'agentCode',
      render: (val) => <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{val || '-'}</span>
    },
    {
      header: 'ตำแหน่ง',
      accessor: 'position',
      render: (val) => <span className="text-gray-500">{val || '-'}</span>
    },
    {
      header: 'สถานภาพตัวแทน',
      accessor: 'agentStatus',
      render: (val) => {
        const statusMeta = getAgentStatusMeta(val);
        return <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusMeta.badge}`}>{statusMeta.label}</span>;
      }
    },
    {
        header: 'ติดต่อ',
        accessor: 'phone',
        render: (val, row) => (
            <div className="flex flex-col text-sm">
                <span className="text-gray-900">{val || '-'}</span>
                {row.lineId && <span className="text-green-600 text-xs">Line: {row.lineId}</span>}
            </div>
        )
    },
    {
        header: 'ใบอนุญาต',
        accessor: 'licenseLevel',
        render: (val) => <span className="text-gray-500">{val || '-'}</span>
    },
    {
        header: 'ทีมงาน',
        accessor: 'downlines',
        render: (_, row) => (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {row.downlines?.length || 0} คน
            </span>
        )
    },
    {
        header: 'กรมธรรม์',
        accessor: 'policies',
        render: (_, row) => (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                {row.policies?.length || 0} ฉบับ
            </span>
        )
    },
    {
      header: 'หัวหน้าทีม',
      accessor: 'managerName', // Virtual accessor for sorting if needed, but we render custom
      render: (_, row) => (
        row.manager ? (
            <button 
                onClick={(e) => { e.stopPropagation(); handleManagerClick(row.manager); }}
                className="text-indigo-600 hover:text-indigo-900 hover:underline font-medium text-left"
            >
                {row.manager.firstName} {row.manager.lastName}
            </button>
        ) : <span className="text-gray-400">-</span>
      )
    },
    {
        header: 'วันที่เข้าร่วม',
        accessor: 'joinDate',
        render: (val) => <span className="text-gray-500">{formatDate(val)}</span>
    },
    {
        header: 'จัดการ',
        accessor: 'actions',
        sortable: false,
        className: 'text-right sticky right-0 bg-white z-10 border-l border-gray-100 shadow-sm',
        headerClassName: 'text-right sticky right-0 bg-gray-50 z-10 border-l border-gray-200 shadow-sm',
        render: (_, row) => (
            <button
                onClick={() => handleViewDetail(row)}
                className="text-gray-400 hover:text-indigo-600 transition-colors"
                title="ดูรายละเอียด"
            >
                <SafeIcon icon={FiEye} className="w-5 h-5" />
            </button>
        )
    }
  ], []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">เรียกดู ลูกข่าย</h1>
        <div className="flex items-center justify-between">
           <p className="text-gray-600">รายการลูกข่าย (ทั้งหมด {filteredAgents.length} คน)</p>
           {filterManager && (
             <button 
               onClick={clearFilter}
               className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-3 py-1.5 rounded-md transition-colors"
             >
               <SafeIcon icon={FiArrowLeft} className="mr-1 w-4 h-4" />
               กลับไปดูทั้งหมด
             </button>
           )}
        </div>
      </div>

      {filterManager && (
         <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex items-center text-indigo-800">
            <SafeIcon icon={FiFilter} className="w-5 h-5 mr-2" />
            <span className="font-medium">
               กำลังแสดงลูกทีมของ: {filterManager.firstName} {filterManager.lastName}
            </span>
            <button onClick={clearFilter} className="ml-auto text-sm underline hover:text-indigo-900">
               ล้างตัวกรอง
            </button>
         </div>
      )}

      <div className="mb-4 flex justify-end">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500">
          <option value="all">สถานะทั้งหมด</option>
          {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex-1 w-full">
           <LoadingSpinner />
        </div>
      ) : (
        <DataTable 
            columns={columns}
            data={filteredAgents}
            searchable={true}
            pagination={true}
            pageSize={10}
        />
      )}

      {/* Full Detail Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleCloseModal}
            ></div>

            {/* Modal panel */}
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl w-full h-[90vh] flex flex-col">
               <div className="bg-white px-4 py-3 border-b flex justify-between items-center sticky top-0 z-10">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  รายละเอียดลูกข่าย
                </h3>
                <button 
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={handleCloseModal}
                >
                  <span className="sr-only">ปิด</span>
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
                {selectedAgent && (
                   <AgentProfileDetail 
                      profile={selectedAgent} 
                      onSave={handleUpdateAgent} 
                      readOnly={false}
                   />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
